// API service for communicating with the backend
import { Transaction, CategorySpending, DashboardStats, mockDashboardStats, mockTransactions } from '../mocks/mockData';

// Use relative path if VITE_API_URL is empty (for platform proxy)
// Fallback to localhost only if undefined
let API_BASE_URL = import.meta.env.VITE_API_URL !== undefined ? import.meta.env.VITE_API_URL : 'https://backend.youware.com';

// Detect if running in YouWare preview environment
const IS_PREVIEW_ENV = typeof window !== 'undefined' && (
  window.location.hostname.includes('preview.yourware.so') || 
  window.location.hostname.includes('preview.youware.app')
);

let useMockData = import.meta.env.VITE_USE_MOCK_DATA === 'true';

// Force disable mock data on production pages.dev to prevent accidental mock mode
if (typeof window !== 'undefined' && window.location.hostname.includes('pages.dev')) {
  useMockData = false;
}

let mockReason = useMockData ? 'Env Var' : '';

// Debug logging for environment variables (only in development)
if (import.meta.env.DEV || IS_PREVIEW_ENV) {
  console.log('API Configuration:', {
    API_BASE_URL,
    useMockData,
    IS_PREVIEW_ENV,
    rawEnvValue: import.meta.env.VITE_USE_MOCK_DATA,
    isString: typeof import.meta.env.VITE_USE_MOCK_DATA,
  });
}

export const isMockDataEnabled = () => useMockData;
export const getMockReason = () => mockReason;

// Get auth headers for YouWare authentication
function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add Project ID for backend routing
  const projectId = import.meta.env.VITE_PROJECT_ID;
  if (projectId) {
    headers['X-Project-Id'] = projectId;
  }

  // YouWare authentication - try to get from localStorage as fallback
  // The platform might inject these, but we add them manually to be safe
  try {
    const encryptedYwid = localStorage.getItem('encrypted_yw_id');
    if (encryptedYwid) {
      headers['X-Encrypted-Yw-ID'] = encryptedYwid;
      headers['X-Is-Login'] = 'true';
    }
  } catch (e) {
    // Ignore localStorage errors
  }

  return headers;
}

// Initialize authentication by fetching user info from platform
export async function initAuth() {
  const urlParams = new URLSearchParams(window.location.search);
  const forceReal = urlParams.get('force_real') === 'true';

  if (useMockData && !forceReal) return;
  if (forceReal) useMockData = false;

  try {
    // 1) Allow explicit user id via query string (useful for debugging)
    const urlYwId = urlParams.get('encrypted_yw_id') || urlParams.get('ywid');
    if (urlYwId) {
      localStorage.setItem('encrypted_yw_id', urlYwId);
      return;
    }

    const host = window.location.hostname;

    // 2) If we're NOT running on a YouWare/YourWare host (e.g., Cloudflare Pages),
    // create a lightweight anonymous user id so the backend can still work.
    const isYouWareHost =
      host.includes('youware') ||
      host.includes('yourware') ||
      host.includes('preview.yourware.so');

    if (!isYouWareHost) {
      let existing = null;
      try {
        existing = localStorage.getItem('encrypted_yw_id');
      } catch (_) {}

      if (!existing) {
        const generated =
          (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
            ? crypto.randomUUID()
            : `anon-${Date.now()}-${Math.random().toString(16).slice(2)}`;

        try {
          localStorage.setItem('encrypted_yw_id', generated);
        } catch (_) {}
      }
      return;
    }

    // 3) YouWare/YourWare: Call the platform's user info endpoint so we can
    // get the real encrypted_yw_id
    const headers: HeadersInit = {};
    const projectId = import.meta.env.VITE_PROJECT_ID;
    if (projectId) {
      headers['X-Project-Id'] = projectId;
    }

    let response = await fetch(`${API_BASE_URL}/__user_info__`, {
      credentials: 'include',
      headers
    });

    let contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/json')) {
      if (!API_BASE_URL || API_BASE_URL.startsWith('/')) {
        const fallbackUrl = 'https://backend.youware.com';
        response = await fetch(`${fallbackUrl}/__user_info__`, {
          credentials: 'include',
          headers
        });

        contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          API_BASE_URL = fallbackUrl;
        }
      }

      if (contentType && !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Received non-JSON response from /__user_info__:', text.substring(0, 100));
        throw new Error('Backend service returned HTML instead of JSON');
      }
    }

    const result = await response.json();

    if (result.code === 0 && result.data) {
      const { encrypted_yw_id } = result.data;
      if (encrypted_yw_id) {
        localStorage.setItem('encrypted_yw_id', encrypted_yw_id);
      }
    }
  } catch (error) {
    console.error('Failed to initialize auth:', error);

    if (IS_PREVIEW_ENV && !forceReal) {
      console.warn('Authentication failed in preview environment. Falling back to mock data.');
      useMockData = true;
      mockReason = 'Auth Failed';
    }
  }
}

// API response wrapper
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // Safety check: if mock data is enabled, never make real API calls
  if (isMockDataEnabled()) {
    throw new Error('Mock data mode is enabled - real API calls should not be made');
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Check if response is OK
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      
      // If we get HTML instead of JSON, it's likely an error page
      if (contentType && contentType.includes('text/html')) {
        const errorText = await response.text().catch(() => '');
        if (errorText.includes('<!doctype') || errorText.includes('<html')) {
          throw new Error(`Backend service unavailable (HTTP ${response.status}). Please check your connection or try again later.`);
        }
      }
      
      // Try to parse as JSON error response
      const errorData = await response.json().catch(() => ({ 
        error: `HTTP ${response.status}: ${response.statusText}` 
      }));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
    }

    // Parse JSON response
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/json')) {
      const responseText = await response.text().catch(() => '');
      if (responseText.includes('<!doctype') || responseText.includes('<html')) {
        throw new Error('Backend service returned HTML instead of JSON. The API endpoint may be unavailable.');
      }
      throw new Error(`Expected JSON response but received ${contentType}`);
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network connection failed. Please check your internet connection and try again.');
    }
    throw error;
  }
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Auth API
export const authAPI = {
  getUser: async () => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for user authentication');
      return Promise.resolve({
        success: true,
        data: {
          user: { id: 'user-1', email: 'user@example.com', name: 'Demo User' },
          households: [
            { 
              id: 'household-1', 
              name: 'Personal Budget', 
              created_at: '2024-01-01T00:00:00Z',
              owner_id: 'user-1',
              currency: 'USD'
            }
          ]
        }
      });
    }
    
    console.log('Attempting real API call for user authentication');
    try {
      return await apiRequest<ApiResponse<{ user: any; households: any[] }>>('/api/auth/user');
    } catch (error: any) {
      // Don't log authentication errors
      if (error.message === 'Authentication required' || error.message?.includes('401')) {
        throw error;
      }
      console.error('Failed to load user data:', error);
      // Return a more user-friendly error
      throw new Error('Failed to load user data. Please check your connection and try again.');
    }
  },
};

// Households API
export const householdsAPI = {
  getHouseholds: () => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for households');
      return Promise.resolve({
        success: true,
        data: {
          households: [
            { 
              id: 'household-1', 
              name: 'Personal Budget', 
              created_at: '2024-01-01T00:00:00Z',
              owner_id: 'user-1',
              currency: 'USD'
            }
          ]
        }
      });
    }
    
    console.log('Attempting real API call for households');
    try {
      return apiRequest<ApiResponse<{ households: any[] }>>('/api/households');
    } catch (error) {
      console.error('Failed to load households:', error);
      throw new Error('Failed to load households. Please check your connection and try again.');
    }
  },
  
  createHousehold: (data: { name: string; currency?: string }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for household creation');
      return Promise.resolve({
        success: true,
        data: {
          household: {
            id: 'household-1',
            name: data.name,
            created_at: new Date().toISOString(),
            owner_id: 'user-1',
            currency: data.currency || 'USD'
          }
        }
      });
    }
    
    console.log('Attempting real API call for household creation');
    try {
      return apiRequest<ApiResponse<{ household: any }>>('/api/households', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to create household:', error);
      throw new Error('Failed to create household. Please check your connection and try again.');
    }
  },

  updateHousehold: (id: string, data: { name?: string; currency?: string; monthly_income?: number; income_amount?: number; income_frequency?: string }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for household update');
      
      // Persist mock income
      if (data.monthly_income !== undefined || data.income_amount !== undefined) {
        localStorage.setItem('mock_monthly_income', (data.income_amount || data.monthly_income || 0).toString());
      }

      return Promise.resolve({
        success: true,
        data: {
          household: {
            id,
            name: data.name || 'Personal Budget',
            created_at: '2024-01-01T00:00:00Z',
            owner_id: 'user-1',
            currency: data.currency || 'USD',
            monthly_income: data.monthly_income,
            income_amount: data.income_amount,
            income_frequency: data.income_frequency || 'monthly'
          }
        }
      });
    }
    
    console.log('Attempting real API call for household update');
    try {
      return apiRequest<ApiResponse<{ household: any }>>(`/api/households/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to update household:', error);
      throw new Error('Failed to update household. Please check your connection and try again.');
    }
  },
};

// Categories API
export const categoriesAPI = {
  getCategories: (householdId: string) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for categories');
      return Promise.resolve({
        success: true,
        data: {
          categories: [
            { 
              id: 'cat-1', 
              name: 'Food & Dining', 
              color: '#FF6B6B', 
              isFixed: false,
              created_at: '2024-01-01T00:00:00Z',
              household_id: householdId
            },
            { 
              id: 'cat-2', 
              name: 'Transportation', 
              color: '#4ECDC4', 
              isFixed: false,
              created_at: '2024-01-01T00:00:00Z',
              household_id: householdId
            }
          ]
        }
      });
    }
    
    console.log('Attempting real API call for categories');
    try {
      return apiRequest<ApiResponse<{ categories: any[] }>>(`/api/households/${householdId}/categories`);
    } catch (error) {
      console.error('Failed to load categories:', error);
      throw new Error('Failed to load categories. Please check your connection and try again.');
    }
  },
  
  createCategory: (householdId: string, data: { name: string; color?: string; is_fixed?: boolean; targetPercentage?: number; limitAmount?: number }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for category creation');
      return Promise.resolve({
        success: true,
        data: {
          category: {
            id: `cat-${Date.now()}`,
            name: data.name,
            color: data.color || '#6366F1',
            isFixed: data.is_fixed || false,
            targetPercentage: data.targetPercentage || 0,
            limitAmount: data.limitAmount || null,
            created_at: new Date().toISOString(),
            household_id: householdId
          }
        }
      });
    }
    
    console.log('Attempting real API call for category creation');
    try {
      return apiRequest<ApiResponse<{ category: any }>>(`/api/households/${householdId}/categories`, {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          target_percentage: data.targetPercentage,
          limit_amount: data.limitAmount
        }),
      });
    } catch (error) {
      console.error('Failed to create category:', error);
      throw new Error('Failed to create category. Please check your connection and try again.');
    }
  },

  updateCategory: (householdId: string, categoryId: string, data: { name?: string; color?: string; targetPercentage?: number; limitAmount?: number }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for category update');
      return Promise.resolve({
        success: true,
        data: {
          category: {
            id: categoryId,
            name: data.name || 'Updated Category',
            color: data.color || '#6366F1',
            isFixed: false,
            targetPercentage: data.targetPercentage,
            limitAmount: data.limitAmount,
            created_at: '2024-01-01T00:00:00Z',
            household_id: householdId
          }
        }
      });
    }
    
    console.log('Attempting real API call for category update');
    try {
      return apiRequest<ApiResponse<{ category: any }>>(`/api/households/${householdId}/categories/${categoryId}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...data,
          target_percentage: data.targetPercentage,
          limit_amount: data.limitAmount
        }),
      });
    } catch (error) {
      console.error('Failed to update category:', error);
      throw new Error('Failed to update category. Please check your connection and try again.');
    }
  },

  deleteCategory: (householdId: string, categoryId: string) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for category deletion');
      return Promise.resolve({
        success: true,
        message: 'Category deleted successfully'
      });
    }
    
    console.log('Attempting real API call for category deletion');
    try {
      return apiRequest<ApiResponse<{ message: string }>>(`/api/households/${householdId}/categories/${categoryId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete category:', error);
      throw new Error('Failed to delete category. Please check your connection and try again.');
    }
  },
};

// Transactions API
export const transactionsAPI = {
  getTransactions: (householdId: string, params?: { limit?: number; offset?: number }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for transactions');
      return Promise.resolve({
        success: true,
        data: {
          transactions: mockTransactions,
          total: mockTransactions.length
        }
      });
    }
    
    const searchParams = new URLSearchParams();
    // If limit is -1, we want all transactions, so we don't set a limit param
    // or set a very high limit if the backend requires it
    if (params?.limit && params.limit !== -1) {
      searchParams.append('limit', params.limit.toString());
    } else if (params?.limit === -1) {
      // For "all" transactions, we might want to set a high limit or no limit
      // Depending on backend implementation. Let's assume no limit param means all
      // or set a high number
      searchParams.append('limit', '1000');
    }
    
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    try {
      return apiRequest<ApiResponse<{ transactions: Transaction[]; total: number }>>(
        `/api/households/${householdId}/transactions${query}`
      );
    } catch (error) {
      console.error('Failed to load transactions:', error);
      throw new Error('Failed to load transactions. Please check your connection and try again.');
    }
  },
  
  createTransaction: (householdId: string, data: {
    amount: number;
    category_id: string;
    description: string;
    merchant: string;
    transaction_date: string;
  }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for transaction creation');
      return Promise.resolve({
        success: true,
        data: {
          transaction: {
            id: `tx-${Date.now()}`,
            amount: data.amount,
            category: data.category_id,
            description: data.description,
            merchant: data.merchant,
            date: data.transaction_date
          }
        }
      });
    }
    
    try {
      return apiRequest<ApiResponse<{ transaction: Transaction }>>(`/api/households/${householdId}/transactions`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to create transaction:', error);
      throw new Error('Failed to create transaction. Please check your connection and try again.');
    }
  },
};

// Stats API
export const statsAPI = {
  getStats: (householdId: string, period = 'this-month') => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for stats');
      
      // Get persisted mock income if available
      const savedIncome = localStorage.getItem('mock_monthly_income');
      const monthlyIncome = savedIncome ? parseFloat(savedIncome) : mockDashboardStats.monthlyIncome;
      const monthlyBudget = savedIncome ? parseFloat(savedIncome) : mockDashboardStats.monthlyBudget;

      return Promise.resolve({
        success: true,
        data: {
          ...mockDashboardStats,
          monthlyIncome,
          monthlyBudget, // In this app, budget is derived from income
          period,
          dateRange: {
            startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
            endDate: new Date().toISOString()
          },
          subscription: { plan: 'free', status: 'active' }
        }
      });
    }
    
    console.log('Attempting real API call for stats');
    const searchParams = new URLSearchParams();
    if (period) searchParams.append('period', period);
    
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    try {
      return apiRequest<ApiResponse<DashboardStats & { 
        period: string;
        dateRange: { startDate: string; endDate: string };
        subscription: { plan: string; status: string };
      }>>(`/api/households/${householdId}/stats${query}`);
    } catch (error) {
      console.error('Failed to load stats:', error);
      throw new Error('Failed to load statistics. Please check your connection and try again.');
    }
  },
};

// AI API
export const aiAPI = {
  categorizeTransaction: (data: { description: string; merchant: string }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for AI categorization');
      // Simple mock categorization based on keywords
      const { description, merchant } = data;
      const text = `${description} ${merchant}`.toLowerCase();
      
      let suggestedCategory = 'Food & Dining';
      let confidence = 0.7;
      let reasoning = 'Based on transaction description and merchant type';
      
      if (text.includes('gas') || text.includes('fuel') || text.includes('shell') || text.includes('bp')) {
        suggestedCategory = 'Transportation';
        confidence = 0.9;
        reasoning = 'Gas station or fuel purchase detected';
      } else if (text.includes('grocery') || text.includes('supermarket') || text.includes('market')) {
        suggestedCategory = 'Food & Dining';
        confidence = 0.95;
        reasoning = 'Grocery store purchase detected';
      } else if (text.includes('restaurant') || text.includes('cafe') || text.includes('pizza') || text.includes('burger')) {
        suggestedCategory = 'Food & Dining';
        confidence = 0.9;
        reasoning = 'Restaurant or dining establishment detected';
      } else if (text.includes('electric') || text.includes('utility') || text.includes('water') || text.includes('internet')) {
        suggestedCategory = 'Bills & Utilities';
        confidence = 0.95;
        reasoning = 'Utility bill payment detected';
      } else if (text.includes('shopping') || text.includes('store') || text.includes('mall')) {
        suggestedCategory = 'Shopping';
        confidence = 0.8;
        reasoning = 'Retail store purchase detected';
      }
      
      return Promise.resolve({
        success: true,
        data: {
          suggestedCategory,
          confidence,
          reasoning
        }
      });
    }
    
    try {
      return apiRequest<ApiResponse<{ 
        suggestedCategory: string;
        confidence: number;
        reasoning: string;
      }>>('/api/ai/categorize', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to categorize transaction:', error);
      throw new Error('Failed to categorize transaction. Please check your connection and try again.');
    }
  },
};

// Billing API
export const billingAPI = {
  createCheckoutSession: (data: { planId: string; householdId: string; billingCycle?: string }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for checkout session');
      // In mock mode, simulate successful checkout redirection
      // Use a local URL instead of a fake Stripe URL to avoid Access Denied errors
      const mockCheckoutUrl = `/settings?success=true&session_id=mock_${Date.now()}&mock_payment=true`;
      return Promise.resolve({
        success: true,
        data: {
          checkoutUrl: mockCheckoutUrl,
          sessionId: `mock_session_${Date.now()}`
        }
      });
    }
    
    try {
      return apiRequest<ApiResponse<{ checkoutUrl: string; sessionId: string }>>('/api/billing/checkout-session', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      throw error;
    }
  },
  
  getCustomerPortal: (data: { householdId: string; returnUrl?: string }) => {
    // Enhanced logic to determine when to use mock data
    const shouldUseMock = isMockDataEnabled();
    
    if (shouldUseMock) {
      console.log('Using mock data for customer portal');
      // In mock mode, simulate successful portal redirection
      const mockPortalUrl = `/settings?portal=true&session_id=mock_portal_${Date.now()}`;
      return Promise.resolve({
        success: true,
        data: {
          portalUrl: mockPortalUrl,
          sessionId: `mock_portal_${Date.now()}`
        }
      });
    }
    
    try {
      return apiRequest<ApiResponse<{ portalUrl: string; sessionId: string }>>('/api/billing/customer-portal', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error('Failed to get customer portal:', error);
      throw new Error('Failed to access customer portal. Please check your connection and try again.');
    }
  },
};